import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { includes, isEmpty } from 'es-toolkit/compat';
import { useSnackbar } from 'notistack';
import React from 'react';
import { ResponseError } from 'superagent';

import {
  AnnotationAssignmentBulkTagAction,
  bulkEditAnnotationAssignment,
  normalizeAnnotationAssignmentTags,
} from 'api/annotationAssignments';
import {
  DialogChromeFooter,
  DialogChromeHeader,
  dialogActionButtonSx,
  dialogPrimaryActionSx,
} from 'components/atoms/Dialog/DialogChrome';
import { useErrorSnackbarWithRetry } from 'components/Snackbars/useErrorSnackbarWithRetry';
import { AnnotationAssignment } from 'interfaces/annotation';
import {
  captureAnnotationAssignmentsQuerySnapshots,
  invalidateAnnotationAssignmentsAcrossCaches,
  isAnnotationAssignmentsQueryKey,
  restoreAnnotationAssignmentsQuerySnapshots,
  updateAnnotationAssignmentsAcrossCaches,
} from 'utils/annotationAssignmentsQueryCache';

const TAG_VALUE_ACTIONS: AnnotationAssignmentBulkTagAction[] = ['add', 'remove', 'override'];
const FREE_SOLO_ACTIONS: AnnotationAssignmentBulkTagAction[] = ['add', 'override'];

const ACTION_LABELS: Record<AnnotationAssignmentBulkTagAction, string> = {
  add: 'Add tags',
  remove: 'Remove selected tags',
  clear: 'Remove all tags',
  override: 'Override tags',
};

const ACTION_DESCRIPTIONS: Record<AnnotationAssignmentBulkTagAction, string> = {
  add: 'Append selected tags to each selected assignment without creating duplicates.',
  remove: 'Remove selected tags from each selected assignment when present.',
  clear: 'Remove every tag from each selected assignment.',
  override: 'Replace each selected assignment tag list with the selected tags.',
};

interface Props {
  annotationAssignmentIdsToExclude?: number[];
  annotationAssignmentIdsToInclude?: number[];
  onClose: () => void;
  open: boolean;
  selectedAssignments: AnnotationAssignment[];
  selectedAssignmentCount: number;
  tagOptions: string[];
}

const actionRequiresTags = (action: AnnotationAssignmentBulkTagAction) => TAG_VALUE_ACTIONS.includes(action);
const actionAllowsFreeSoloTags = (action: AnnotationAssignmentBulkTagAction) => FREE_SOLO_ACTIONS.includes(action);

const getTagsPresentInAnySelectedAssignment = (selectedAssignments: AnnotationAssignment[]) => {
  const selectedTags = new Set<string>();

  selectedAssignments.forEach((assignment) => {
    (assignment.tags || []).forEach((tag) => selectedTags.add(tag));
  });

  return selectedTags;
};

const getTagsPresentInEverySelectedAssignment = (selectedAssignments: AnnotationAssignment[]) => {
  if (isEmpty(selectedAssignments)) {
    return new Set<string>();
  }

  const [firstAssignment, ...remainingAssignments] = selectedAssignments;
  const commonTags = new Set(firstAssignment.tags || []);

  remainingAssignments.forEach((assignment) => {
    const assignmentTags = new Set(assignment.tags || []);
    commonTags.forEach((tag) => {
      if (!assignmentTags.has(tag)) {
        commonTags.delete(tag);
      }
    });
  });

  return commonTags;
};

const getSelectedAwareTagOptions = (
  tagOptions: string[],
  selectedAssignments: AnnotationAssignment[],
  tagAction: AnnotationAssignmentBulkTagAction
) => {
  if (tagAction === 'add') {
    const tagsAssignedToEverySelection = getTagsPresentInEverySelectedAssignment(selectedAssignments);
    return tagOptions.filter((tag) => !tagsAssignedToEverySelection.has(tag));
  }

  if (tagAction === 'remove') {
    const tagsAssignedToAnySelection = getTagsPresentInAnySelectedAssignment(selectedAssignments);
    return tagOptions.filter((tag) => tagsAssignedToAnySelection.has(tag));
  }

  return tagOptions;
};

const getValidTagsForAction = (
  tags: string[],
  tagOptions: string[],
  selectedAssignments: AnnotationAssignment[],
  tagAction: AnnotationAssignmentBulkTagAction
) => {
  const selectedAwareTagOptions = getSelectedAwareTagOptions(tagOptions, selectedAssignments, tagAction);
  return tags.filter((tag) => selectedAwareTagOptions.includes(tag));
};

type BulkTagMutationVariables = Parameters<typeof bulkEditAnnotationAssignment>[0];

const isAssignmentInSelectedScope = (
  assignment: AnnotationAssignment,
  annotationAssignmentIdsToInclude?: number[],
  annotationAssignmentIdsToExclude?: number[]
) => {
  const assignmentId = assignment.annotationAssignmentId;
  const isExcluded = includes(annotationAssignmentIdsToExclude, assignmentId);

  if (annotationAssignmentIdsToInclude) {
    return includes(annotationAssignmentIdsToInclude, assignmentId) && !isExcluded;
  }

  return Boolean(annotationAssignmentIdsToExclude) && !isExcluded;
};

const applyBulkTagAction = (
  currentTags: string[] | undefined,
  tags: string[],
  tagAction: AnnotationAssignmentBulkTagAction
) => {
  const existingTags = currentTags || [];

  if (tagAction === 'add') {
    const seenTags = new Set(existingTags);
    return tags.reduce<string[]>(
      (nextTags, tag) => {
        if (!seenTags.has(tag)) {
          seenTags.add(tag);
          nextTags.push(tag);
        }

        return nextTags;
      },
      [...existingTags]
    );
  }

  if (tagAction === 'remove') {
    const tagsToRemove = new Set(tags);
    return existingTags.filter((tag) => !tagsToRemove.has(tag));
  }

  if (tagAction === 'clear') {
    return [];
  }

  return tags;
};

export const BulkTagActionDialog: React.FC<Props> = ({
  annotationAssignmentIdsToExclude,
  annotationAssignmentIdsToInclude,
  onClose,
  open,
  selectedAssignments,
  selectedAssignmentCount,
  tagOptions,
}) => {
  const [tagAction, setTagAction] = React.useState<AnnotationAssignmentBulkTagAction>('add');
  const [tags, setTags] = React.useState<string[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbarWithRetry();
  const queryClient = useQueryClient();

  const normalizedTags = normalizeAnnotationAssignmentTags(tags);
  const hasSelectionScope = Boolean(annotationAssignmentIdsToInclude || annotationAssignmentIdsToExclude);
  const requiresTags = actionRequiresTags(tagAction);
  const submissionDisabled = !hasSelectionScope || (requiresTags && isEmpty(normalizedTags));
  const selectedAwareTagOptions = getSelectedAwareTagOptions(tagOptions, selectedAssignments, tagAction);

  const mutation = useMutation({
    mutationFn: (variables: BulkTagMutationVariables) => bulkEditAnnotationAssignment(variables),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        predicate: (query) => isAnnotationAssignmentsQueryKey(query.queryKey),
      });

      const snapshots = captureAnnotationAssignmentsQuerySnapshots(queryClient);

      updateAnnotationAssignmentsAcrossCaches(queryClient, (assignment) => {
        if (
          !isAssignmentInSelectedScope(
            assignment,
            variables.annotationAssignmentIdsToInclude,
            variables.annotationAssignmentIdsToExclude
          )
        ) {
          return assignment;
        }

        return {
          ...assignment,
          tags: applyBulkTagAction(assignment.tags, variables.tags || [], variables.tagAction),
        };
      });

      return { snapshots };
    },
    onError: (error, _variables, context) => {
      restoreAnnotationAssignmentsQuerySnapshots(queryClient, context?.snapshots || []);
      enqueueErrorSnackbar(error as ResponseError, 'bulk update annotation assignment tags');
    },
    onSuccess: (_data, variables) => {
      invalidateAnnotationAssignmentsAcrossCaches(queryClient);
      enqueueSnackbar({
        message: `${ACTION_LABELS[variables.tagAction]} applied to ${selectedAssignmentCount} assignment${
          selectedAssignmentCount === 1 ? '' : 's'
        }`,
        variant: 'success',
      });
      setTags([]);
      setTagAction('add');
      onClose();
    },
  });

  const handleClose = () => {
    if (!mutation.isLoading) {
      setTags([]);
      setTagAction('add');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" aria-labelledby="bulk-tag-action-dialog-title">
      <DialogChromeHeader
        title="Bulk tag actions"
        titleId="bulk-tag-action-dialog-title"
        subtitle={`${selectedAssignmentCount} selected assignment${selectedAssignmentCount === 1 ? '' : 's'}`}
        onClose={handleClose}
        sticky
      />
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <RadioGroup
            value={tagAction}
            onChange={(_, value) => {
              const nextAction = value as AnnotationAssignmentBulkTagAction;
              setTagAction(nextAction);
              if (!actionRequiresTags(nextAction)) {
                setTags([]);
              } else if (!actionAllowsFreeSoloTags(nextAction)) {
                setTags((currentTags) =>
                  getValidTagsForAction(currentTags, tagOptions, selectedAssignments, nextAction)
                );
              }
            }}
          >
            {(Object.keys(ACTION_LABELS) as AnnotationAssignmentBulkTagAction[]).map((action) => (
              <FormControlLabel
                key={action}
                value={action}
                control={<Radio />}
                label={
                  <Stack spacing={0.25}>
                    <Typography variant="body1">{ACTION_LABELS[action]}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ACTION_DESCRIPTIONS[action]}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </RadioGroup>

          {requiresTags ? (
            <Autocomplete
              multiple
              freeSolo={actionAllowsFreeSoloTags(tagAction)}
              options={selectedAwareTagOptions}
              value={tags}
              onChange={(_, newValue) => setTags(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  helperText={isEmpty(normalizedTags) ? 'Select or enter at least one tag.' : undefined}
                />
              )}
            />
          ) : (
            <Alert severity="warning">This action removes all tags from the selected assignments.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogChromeFooter sticky>
        <Button onClick={handleClose} disabled={mutation.isLoading} sx={dialogActionButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<LocalOfferOutlinedIcon />}
          disabled={submissionDisabled || mutation.isLoading}
          onClick={() =>
            mutation.mutate({
              annotationAssignmentIdsToExclude,
              annotationAssignmentIdsToInclude,
              tagAction,
              tags: normalizedTags,
            })
          }
          sx={dialogPrimaryActionSx}
        >
          {ACTION_LABELS[tagAction]}
        </Button>
      </DialogChromeFooter>
    </Dialog>
  );
};
